<template>
  <div
    class="page-container"
    v-loading="loading"
    element-loading-text="拼命加载中"
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0)">
    <div class="header">
      <div class="title">创建收款单</div>
    </div>
    <div class="wrapper">
      <p class="sub-title">基础信息</p>
      <el-form class="form-content" :model="form" :rules="rules" ref="form" label-width="135px">
        <el-form-item label="收款单模式" prop="createType">
          <el-radio-group v-model="form.createType">
            <el-radio :label="0">手动创建</el-radio>
            <el-radio :label="1">导入文件</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.createType === 0" label="收款单金额" prop="type">
          <el-radio-group v-model="form.type">
            <el-radio :label="0">固定金额</el-radio>
            <el-radio :label="1">消费者自填金额</el-radio>
          </el-radio-group>
          <p class="yellow-tips">注：{{ form.type === 0 ? "由商家预设固定的收款金额，消费者无需填写" : "商家无需预设固定的收款金额，由消费者支付时自行填写" }}</p>
        </el-form-item>
        <el-form-item label="关联收银点" prop="cashierAreaNo">
          <el-select v-model="form.cashierAreaNo" placeholder="请选择关联收银点" filterable clearable>
            <el-option
              v-for="item in cashierPointList"
              :key="item.cashierAreaNo"
              :label="`${item.cashierAreaName}(${item.cashierAreaNo})`"
              :value="item.cashierAreaNo"/>
          </el-select>
        </el-form-item>
        <el-form-item label="收款单名称" prop="receiptName">
          <el-input v-model="form.receiptName" placeholder="请输入收款单名称" clearable @input="handleInputChange"/>
        </el-form-item>
        <el-form-item v-if="form.type === 0 && form.createType === 0" label="收款金额" prop="receiptAmount">
          <el-input
            v-model="form.receiptAmount"
            class="common-input"
            v-limit-input-number="{ limitNumber: 2 }"
            placeholder="请输入收款金额"
            :maxlength="9"/>
        </el-form-item>
        <el-form-item v-if="form.createType === 0" label="收款单有效时间" prop="validType">
          <el-radio-group v-model="form.validType">
            <el-radio :label="1">自定义</el-radio>
            <el-radio :label="0">长期有效</el-radio>
          </el-radio-group>
          <div v-if="form.validType === 1">
            <el-input
              v-model="form.validDays"
              v-limit-input-number="{ limitNumber: 0 }"
              :maxlength="3"
              placeholder="请输入收款单有效时间">
              <template #append>天</template>
            </el-input>
          </div>
        </el-form-item>
        <el-form-item v-if="form.createType === 0" label="收款单笔数上限" prop="receiptNumberType">
          <el-radio-group v-model="form.receiptNumberType">
            <el-radio :label="1">自定义</el-radio>
            <el-radio :label="0">无上限</el-radio>
          </el-radio-group>
          <div v-if="form.receiptNumberType === 1">
            <el-input
              v-model="form.receiptNumberUpperLimit"
              v-limit-input-number="{ limitNumber: 0 }"
              :maxlength="6"
              placeholder="请输入收款笔数上限">
              <template #append>笔</template>
            </el-input>
          </div>
        </el-form-item>
        <el-form-item v-if="form.createType === 0" label="是否设置付款密码" prop="setReceivePwdFlag">
          <el-radio-group v-model="form.setReceivePwdFlag">
            <el-radio :label="1">是</el-radio>
            <el-radio :label="0">否</el-radio>
          </el-radio-group>
          <div v-if="form.setReceivePwdFlag === 1">
            <el-input
              v-model="form.receivePwd"
              v-limit-input-number="{ limitNumber: 0 }"
              :maxlength="6"
              placeholder="请设置收款密码"/>
          </div>
        </el-form-item>
      </el-form>
      <div class="line"/>
      <manually-create
        ref="manuallyCreate"
        v-if="form.createType === 0"
        :type="form.type"
        :template-type="templateType"
        :money-mode="form.type"/>
      <import-create ref="importCreate" v-if="form.createType === 1" :template-type="templateType"/>
      <div class="line"/>
      <el-button class="bs-btn2" type="primary" @click="handleCancel">取消</el-button>
      <el-button class="bs-btn" type="primary" @click="handleCommitCreate">创建</el-button>
    </div>
    <qrcode-dialog
      :show-dialog="showSuccessDialog"
      :receiptOrderNo="qrDialogInfo.receiptOrderNo"
      :receiptName="qrDialogInfo.receiptName"
      :merchantName="qrDialogInfo.merchantName"
      :is-create="true"
      @close="handleCloseDialog"/>
  </div>
</template>

<script>
import { createReceiveOrder } from "@/api/collection-bill";
import { getUrlParams } from "@/utils/index";
import { fetchCashiers } from "@/api/flow";
import qrcodeDialog from "./qrcodeDialog.vue";
import manuallyCreate from "./manuallyCreate.vue";
import importCreate from "./importCreate.vue";
import { multiply } from "../../utils/math";

export default {
  name: "collection-bill-create",
  components: {
    qrcodeDialog,
    manuallyCreate,
    importCreate,
  },
  data() {
    const validTypeFunc = (rule, value, callback) => {
      const { validDays } = this.form;
      const days = Number.parseInt(validDays, 0);
      if (value === 1 && (!Number(validDays) || days > 365)) {
        callback(new Error("收款单有效时间为1至365之间的数"));
      } else {
        callback();
      }
    };
    const receiptNumberTypeFunc = (rule, value, callback) => {
      const { receiptNumberUpperLimit } = this.form;
      if (value === 1 && !Number(receiptNumberUpperLimit)) {
        callback(new Error("请输入收款单笔数上限"));
      } else {
        callback();
      }
    };
    const setReceivePwdFlagFunc = (rule, value, callback) => {
      const { receivePwd } = this.form;
      if (value === 1 && receivePwd.length < 6) {
        callback(new Error("请设置6位数密码"));
      } else {
        callback();
      }
    };
    return {
      loading: false,
      form: {
        createType: 0,
        type: 0,
        receiptAmount: "",
        receiptName: "",
        cashierAreaNo: "",
        validType: 0,
        receiptNumberType: 0,
        setReceivePwdFlag: 0,
        validDays: "",
        receiptNumberUpperLimit: "",
        receivePwd: "",
      },
      cashierPointList: [],
      qrcodeImg: "",
      templateType: -1,
      rules: {
        cashierAreaNo: [{ required: true, message: '请选择关联收银点', trigger: ['blur', 'change'] }],
        receiptName: [{ required: true, message: '请输入收款单名称', trigger: ['blur', 'change'] }],
        receiptAmount: [{ required: true, message: '请输入收款金额', trigger: ['blur', 'change'] }],
        validType: [{ required: true, validator: validTypeFunc, trigger: ['blur', 'change'] }],
        receiptNumberType: [{ required: true, validator: receiptNumberTypeFunc, trigger: ['blur', 'change'] }],
        setReceivePwdFlag: [{ required: true, validator: setReceivePwdFlagFunc, trigger: ['blur', 'change'] }],
      },
      showSuccessDialog: false,
      qrDialogInfo: {},
    };
  },
  computed: {
    loginInfo() {
      return JSON.parse(window.localStorage.getItem('LOGIN_INFO'));
    },
  },
  activated() {
    const params = getUrlParams();
    if (params.reset) {
      this.init();
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      const params = getUrlParams();
      this.form = {
        createType: 1,
        type: 0,
        receiptAmount: "",
        receiptName: "",
        cashierAreaNo: "",
        validType: 0,
        receiptNumberType: 0,
        setReceivePwdFlag: 0,
        validDays: "",
        receiptNumberUpperLimit: "",
        receivePwd: "",
      };
      this.qrcodeImg = "";
      this.templateType = Number.parseInt(params.mode, 0);
      if (!this.cashierPointList.length) {
        this.getCashierPointList();
      }
      this.$nextTick(() => {
        this.$refs.importCreate.handleReset();
      });
    },
    async getCashierPointList() {
      const res = await fetchCashiers([]);
      this.cashierPointList = res.data.data.list;
    },
    handleInputChange(val) {
      let len = 0;
      let str = '';
      val.split('').forEach((item) => {
        const charCode = item.charCodeAt(0); // unicode编码
        len += charCode >= 0 && charCode <= 128 ? 1 : 2;
        if (len <= 30) str += item;
      });
      this.form.receiptName = str;
    },
    handleCancel() {
      this.$confirm("是否取消收款单创建", "提示", {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        cancelButtonClass: "bs-btn2",
        confirmButtonClass: "bs-btn",
      }).then(() => {
        this.createEnd();
      });
    },
    handleCommitCreate() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          const { merchantId } = this.loginInfo;
          const {
            createType,
            type,
            receiptAmount,
            receiptName,
            cashierAreaNo,
            validType,
            receiptNumberType,
            setReceivePwdFlag,
            validDays,
            receiptNumberUpperLimit,
            receivePwd,
          } = this.form;
          const params = {
            merchantId,
            createType,
            cashierAreaNo,
            receiptName,
          };
          if (createType === 0) {
            params.validType = validType;
            params.receiptNumberType = receiptNumberType;
            params.setReceivePwdFlag = setReceivePwdFlag;
            params.type = type;
            if (type === 0) {
              params.receiptAmount = multiply(receiptAmount, 100);
            }
            if (validType === 1) {
              params.validDays = validDays;
            }
            if (receiptNumberType === 1) {
              params.receiptNumberUpperLimit = receiptNumberUpperLimit;
            }
            if (setReceivePwdFlag === 1) {
              params.receivePwd = receivePwd;
            }
            params.orderRemarkList = await this.$refs.manuallyCreate.getListParam();
            if (params.orderRemarkList.length !== 0) {
              this.handleCreateBill(params);
            }
          } else {
            params.exportDtoList = this.$refs.importCreate.getListParam();
            if (params.exportDtoList.length === 0) {
              this.$message.warning('请上传文件');
            } else {
              this.handleCreateBill(params);
            }
          }
        }
      });
    },
    handleCreateBill(params) {
      this.$confirm("是否确认创建该收款单", "提示", {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        cancelButtonClass: "bs-btn2",
        confirmButtonClass: "bs-btn",
      }).then(() => {
        this.loading = true;
        createReceiveOrder(params).then((res) => {
          if (res.data.error_code === 0) {
            this.showSuccessDialog = true;
            this.qrDialogInfo = res.data.data;
          }
        }).finally(() => {
          this.loading = false;
        });
      });
    },
    createEnd() {
      this.$store.dispatch("app/delVisitedViews", {
        name: 'collection-bill-create',
        path: '/collection-bill/create',
        title: '创建收款单',
      });
      this.$router.replace('/collection-bill/index');
    },
    handleCloseDialog() {
      this.showSuccessDialog = false;
      this.createEnd();
    },
  },
};
</script>

<style scoped lang="scss">
.page-container {
  > .header {
    padding-left: 50px;
    background-color: #fff;

    .title {
      position: relative;
      height: 80px;
      font-size: 18px;
      line-height: 80px;
      color: #333;

      &::after {
        position: absolute;
        right: 0;
        bottom: 0;
        left: -50px;
        display: block;
        height: 0;
        content: "";
        border-bottom: 1px solid #f2f3f3;
      }
    }
  }

  .sub-title {
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: bold;
    color: #303133;
  }

  .yellow-tips {
    font-size: 12px;
    line-height: 12px;
    color: #ff8e2a;
  }

  .wrapper {
    padding: 30px 40px 40px;

    .form-content {
      /deep/ .el-select,
      /deep/ .el-input {
        width: 260px;
      }
    }

    .line {
      margin: 20px 0;
      border-top: 1px solid #f5f7fa;
    }
  }
}
</style>
